import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Building2, CalendarPlus, Download, MessageSquareText, Phone, Star } from "lucide-react";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { CalendlyLink, SmartLink } from "./SiteHeader";
import { Testimonials, useReviewsLabel } from "./Testimonials";
import { PHONE, PHONE_TEL } from "./nav";
import { MATT } from "./ExperiencePage";
import { readUtm } from "./BookPage";
import { API, TZ, TZ_LABEL, recallBooking, rememberBooking } from "./booking/bookingParts";
import { ManageBooking } from "./booking/ManageBooking";
import { getVisitorId, newMetaEventId, pixelTrack, rememberVisitor } from "../lib/metaEvent";
import "./booked.css";

const SMS = "sms:+17809002495";
const CREDS = ["Independent", "Licensed in Alberta (RECA)", "Powered by BRX Mortgage"];
let confirmedFor = null;  // StrictMode re-runs effects in dev; one legacy confirmation per visit

// Per-intent copy. A discovery call sets direction; the Strategy Plan comes after the application — never promise the plan here.
const ANSWERED = "A clear next step";
const ANSWERED_P = "You’ll leave knowing what to do next and how our team can help you move forward.";
const COPY = {
  buying: { topic: "your next home", card: "Your home-buying call", together: "Buying with someone? They’re welcome to join the conversation.",
    chat: "We’ll talk about when you’d like to buy, where you’re starting from, and the questions on your mind.",
    steps: [["Your plans first", "We’ll talk about when you’d like to buy, where you’re starting, and what you’d like help figuring out."], ["Your questions, answered", "From “How much could I borrow?” to “What happens after an offer?”—you don’t need to know mortgage terminology to have this conversation."], [ANSWERED, ANSWERED_P]] },
  first_time: { topic: "your first home", card: "Your first home call", together: "Buying with someone? They’re welcome to join the conversation.",
    chat: "We’ll talk about your buying plans, your down payment plans, and the questions on your mind.",
    steps: [["Your plans first", "We’ll talk about when you’d like to buy, where you’re starting, and what you’d like help figuring out."], ["Your questions, answered", "From “What counts as a down payment?” to “Where do I even start?”—you don’t need to know mortgage terminology to have this conversation."], [ANSWERED, ANSWERED_P]] },
  renewal: { topic: "your renewal", card: "Your renewal call", together: "Renewing with someone? They’re welcome to join the conversation.",
    chat: "We’ll talk about your current mortgage, when your term ends, and what you’d like to be different this time around.",
    steps: [["Your renewal first", "We’ll talk about your current mortgage, when your term ends, and what you’d like to change."], ["Your questions, answered", "From “Should I just sign the letter?” to “What does switching lenders involve?”—plain language, no pressure."], [ANSWERED, ANSWERED_P]] },
  refinance: { topic: "your refinance", card: "Your refinance call", together: "On title with someone? They’re welcome to join the conversation.",
    chat: "We’ll talk about what you’d like the refinance to do for you, your current mortgage, and the questions on your mind.",
    steps: [["Your goals first", "We’ll talk about what you’d like to achieve—a lower payment, consolidating debt or using your equity—and where you’re starting from."], ["Your questions, answered", "From “What would it cost to break my mortgage?” to “Does this even make sense for me?”—you don’t need to know mortgage terminology to have this conversation."], [ANSWERED, ANSWERED_P]] },
  payment_relief: { topic: "your payments", card: "Your payment relief call", together: "Sharing the mortgage with someone? They’re welcome to join the conversation.",
    chat: "We’ll talk about your mortgage, what’s feeling tight right now, and the questions on your mind.",
    steps: [["Your situation first", "We’ll talk about your mortgage, your other payments, and what you’d like to feel different."], ["Your questions, answered", "From “What are my options?” to “Will this affect my credit?”—plain language, no judgment."], [ANSWERED, ANSWERED_P]] },
  realtor: { topic: "your clients", card: "Your partnership call", together: "Part of a team? Bring them along.", realtor: true,
    chat: "We’ll talk about your business, the markets you work, and how we can support your clients.",
    steps: [["Your business first", "We’ll talk about the markets you work, your clients, and what you’d like from a lending partner."], ["Your questions, answered", "From turnaround times to how we keep you in the loop—ask anything."], ["A clear next step", "You’ll leave knowing how a partnership would work and what happens next."]] },
};
COPY.default = { ...COPY.buying, topic: "your mortgage", card: "Your call" };

const calStamp = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
const calLinks = (startIso, duration, first) => {
  const s = new Date(startIso);
  if (Number.isNaN(s.getTime())) return null;
  const e = new Date(s.getTime() + (duration || 15) * 60000);
  const title = "Mortgage call · Hello Mortgage Team";
  const details = `We call you${first ? `, ${first}` : ""} from ${PHONE}. Just bring your questions — nothing to prepare. Need another time? Use the reschedule link in your confirmation email.`;
  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${calStamp(s)}/${calStamp(e)}&details=${encodeURIComponent(details)}`;
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Hello Mortgage Team//Booking//EN", "BEGIN:VEVENT", `UID:${calStamp(s)}@hellomortgage.ca`, `DTSTAMP:${calStamp(new Date())}`, `DTSTART:${calStamp(s)}`, `DTEND:${calStamp(e)}`, `SUMMARY:${title}`, `DESCRIPTION:${details.replace(/,/g, "\\,")}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  return { google, ics: `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}` };
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric", timeZone: TZ });
const fmtClock = (iso) => new Date(iso).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", timeZone: TZ });
const tzLabel = () => TZ_LABEL;
const fmtPhone = (p) => { const d = (p || "").replace(/\D/g, ""); return d.length === 11 && d[0] === "1" ? `${d.slice(1, 4)}-${d.slice(4, 7)}-${d.slice(7)}` : p; };

// Calendly's "pass event details to redirect" (legacy direct links) only gives name/email/start.
const legacyFromQuery = (q) => {
  const name = q.get("invitee_full_name") || "";
  if (!name && !q.get("invitee_email") && !q.get("event_start_time")) return null;
  return { legacy: true, first_name: name.split(" ")[0] || null, start_time: q.get("event_start_time"), duration: 15, context: null };
};

function useLegacyConfirm(q, search) {
  useEffect(() => {
    const email = q.get("invitee_email"); const name = q.get("invitee_full_name") || "";
    if (q.get("b") || (!email && !name) || confirmedFor === search) return;
    confirmedFor = search;
    const [first, ...rest] = name.split(" ");
    rememberVisitor({ email, firstName: first, lastName: rest.join(" ") });
    let id = null;
    try { id = sessionStorage.getItem("hm_book_event_id"); } catch { /* storage blocked */ }
    const alreadyFired = !!id;
    if (!id) { id = newMetaEventId(); pixelTrack("Lead", { content_name: "book_call" }, id); }
    fetch(`${API}/api/public/book/confirmed`, {
      method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true, credentials: "include",
      body: JSON.stringify({ event_id: id, invitee_name: name || null, invitee_email: email || null, event_start_time: q.get("event_start_time"), utm: readUtm(search), url: window.location.href, external_id: getVisitorId() }),
    }).catch(() => {});
    if (alreadyFired) { try { sessionStorage.removeItem("hm_book_event_id"); } catch { /* ignore */ } }
  }, [q, search]);
}

function PhoneLine({ booking, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const shown = booking.phone ? fmtPhone(booking.phone) : booking.phone_masked;
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    const r = await fetch(`${API}/api/public/booking/confirmed/${encodeURIComponent(booking.booking_id)}/phone`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: val }) });
    const data = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { setErr(data.detail || "That number doesn’t look complete."); return; }
    onUpdated(data); setEditing(false); setVal("");
  };
  if (editing) {
    return (
      <form className="bkd-phone-form" onSubmit={save} data-testid="booked-phone-form">
        <label>New phone number<input type="tel" inputMode="tel" required minLength={10} value={val} onChange={(e) => setVal(e.target.value)} placeholder="780 555 0123" autoFocus data-testid="booked-phone-input" /></label>
        {err && <div className="bkd-phone-err" role="alert" data-testid="booked-phone-error">{err}</div>}
        <div><button type="submit" className="btn" disabled={busy} data-testid="booked-phone-save">{busy ? "Saving…" : "Save number"}</button><button type="button" className="bkd-textbtn" onClick={() => setEditing(false)} data-testid="booked-phone-cancel">Cancel</button></div>
      </form>
    );
  }
  return (
    <p className="bkd-calls" data-testid="booked-calls-to"><Phone size={18} aria-hidden="true" /><span>
      {shown ? <>{booking.persona === "matt" ? "Matt will" : "We’ll"} call you at <strong data-testid="booked-phone">{shown}</strong>.</> : <>{booking.persona === "matt" ? "Matt will" : "We’ll"} call you at the number you gave us.</>}
      {booking.booking_id && !booking.legacy && <> <button type="button" className="bkd-textbtn" onClick={() => setEditing(true)} data-testid="booked-phone-edit">Update your phone number</button></>}
    </span></p>
  );
}

function AppointmentCard({ booking, copy, onUpdated }) {
  const first = booking.first_name;
  const ok = booking.start_time && !Number.isNaN(new Date(booking.start_time).getTime());
  const cancelled = booking.status === "cancelled";
  const cal = ok && !cancelled ? calLinks(booking.start_time, booking.duration, first) : null;
  return (
    <div className={`bkd-card${cancelled ? " cancelled" : ""}`} data-testid="booked-card">
      <img className="bkd-key" src="/brand/feel/celebrated.png" alt="" aria-hidden="true" width="150" height="168" />
      <div className="bkd-card-top"><img src="/brand/hello-mortgage-team-logo-transparent.png" alt="Hello Mortgage Team" width="150" height="48" /></div>
      <div className="bkd-card-label" data-testid="booked-card-label">{cancelled ? "Cancelled" : booking.persona === "matt" ? `${copy.card} with Matt` : `${copy.card} details`}</div>
      {ok ? <>
        <div className="bkd-date" data-testid="booked-date">{fmtDate(booking.start_time)}</div>
        <div className="bkd-time" data-testid="booked-time"><strong>{fmtClock(booking.start_time)}</strong> <span>{tzLabel(booking.start_time)} · {booking.duration || 15} minutes</span></div>
      </> : <div className="bkd-date" data-testid="booked-date">Your call is confirmed.</div>}
      {!cancelled && <>
        <PhoneLine booking={booking} onUpdated={onUpdated} />
        <p className="bkd-calls bkd-calls-from" data-testid="booked-calls-from"><span>The call will come from <a href={PHONE_TEL}>{PHONE}</a>. Save the number so you recognize it.</span></p>
      </>}
      {cal && (
        <div className="bkd-cal-row" data-testid="booked-calendar-links">
          <a className="btn outline" href={cal.google} target="_blank" rel="noopener noreferrer" data-testid="booked-add-google"><CalendarPlus size={18} aria-hidden="true" /> Google Calendar</a>
          <a className="btn outline" href={cal.ics} download="hello-mortgage-call.ics" data-testid="booked-add-ics"><Download size={18} aria-hidden="true" /> Apple / Outlook</a>
        </div>
      )}
      {booking.booking_id && !booking.legacy ? <ManageBooking booking={booking} onUpdated={onUpdated} />
        : booking.reschedule_url && <p className="bkd-fine bkd-move">Need another time? <a href={booking.reschedule_url} target="_blank" rel="noopener noreferrer" data-testid="booked-reschedule">Reschedule</a>{booking.cancel_url && <> · <a href={booking.cancel_url} target="_blank" rel="noopener noreferrer" data-testid="booked-cancel">Cancel</a></>}</p>}
      {!cancelled && <p className="bkd-fine">Check your inbox for your confirmation and calendar invitation. Can’t see it? Check your junk folder.</p>}
    </div>
  );
}

function Modules({ confirm }) {
  const calcs = confirm?.calcs || []; const articles = confirm?.articles || [];
  if (!calcs.length && !articles.length) return null;
  return (
    <div className="bkd-modules" data-testid="booked-modules">
      {calcs.map(([href, name, blurb]) => <SmartLink className="bkd-module" href={href} key={href} data-testid={`booked-calc-${name.toLowerCase().replace(/\s+/g, "-")}`}><small>Calculator</small><b>{name}</b><p>{blurb}</p><ArrowUpRight size={18} aria-hidden="true" /></SmartLink>)}
      {articles.map(([href, name, blurb]) => <SmartLink className="bkd-module" href={href} key={href}><small>Read</small><b>{name}</b>{blurb && <p>{blurb}</p>}<ArrowUpRight size={18} aria-hidden="true" /></SmartLink>)}
    </div>
  );
}

function EmptyState() {
  return (
    <section className="bkd-hero" data-testid="booked-empty">
      <div className="wrap bkd-hero-grid">
        <div>
          <div className="eyebrow">Hmm.</div>
          <h1 className="display">We can’t find that booking.</h1>
          <p className="bkd-sub">The link may have expired, or the call was booked directly through Calendly — in which case your calendar invitation has everything you need. Want to pick a time instead?</p>
          <div className="actions"><CalendlyLink className="btn" testId="booked-empty-book">Pick a time</CalendlyLink><a className="textlink" href={SMS} data-testid="booked-empty-text">Or text us</a></div>
        </div>
      </div>
    </section>
  );
}

export default function BookedPage() {
  const { search, state } = useLocation();
  const reviews = useReviewsLabel();
  const q = useMemo(() => new URLSearchParams(search), [search]);
  const id = q.get("b");
  const [booking, setBooking] = useState(() => state?.booking || (id && recallBooking(id)) || legacyFromQuery(q));
  const [status, setStatus] = useState(booking ? "ok" : id ? "loading" : "empty");
  useLegacyConfirm(q, search);
  useEffect(() => {
    if (booking || !id) return;
    fetch(`${API}/api/public/booking/confirmed/${encodeURIComponent(id)}`).then((r) => (r.ok ? r.json() : Promise.reject(r))).then((b) => { setBooking(b); setStatus("ok"); }).catch(() => setStatus("empty"));
  }, [id, booking]);
  const onUpdated = (b) => { setBooking(b); rememberBooking(b); };

  const ctx = booking?.context || null;
  const copy = COPY[ctx?.key] || COPY.default;
  const first = booking?.first_name;
  const duration = booking?.duration || 15;
  const savings = booking?.renewal?.has_savings ? Math.round(booking.renewal.monthly_savings || 0) : 0;
  const cancelled = booking?.status === "cancelled";

  return (
    <SiteLayout minimal bodyClass="booked-page">
      <SeoHead title="You’re booked — Hello Mortgage Team" description="Your call is confirmed. Here’s what happens next." path="/booked/" noindex />
      {status === "loading" && <section className="bkd-hero"><div className="wrap"><div className="bkd-loading" data-testid="booked-loading">Finding your call…</div></div></section>}
      {status === "empty" && <EmptyState />}
      {status === "ok" && booking && (
        <div data-testid={`booked-page${ctx ? `-${ctx.key}` : ""}`}>
          <section className="bkd-hero">
            <div className="wrap bkd-hero-grid">
              <div className="bkd-hero-copy">
                <div className="eyebrow" data-testid="booked-eyebrow">{cancelled ? "Call cancelled" : "You’re booked."}</div>
                <h1 className="display" data-testid="booked-h1">{cancelled ? (first ? `${first}, your call is cancelled.` : "Your call is cancelled.") : first ? `${first}, let’s talk about ${copy.topic}.` : `Let’s talk about ${copy.topic}.`}</h1>
                <p className="bkd-sub" data-testid="booked-subhead">{cancelled ? "No hard feelings. Whenever you’d like to talk, pick a new time and we’ll call you." : `Your ${duration}-minute phone call${booking.persona === "matt" ? " with Matt" : ""} is confirmed. We’ll talk through your plans, answer your questions, and help you understand your next step.`}</p>
                {savings > 0 && !cancelled && <div className="bkd-recap" data-testid="booked-renewal-recap">Your Renewal Checker found about <b>${savings.toLocaleString("en-CA")}/month</b> in potential savings. We’ll talk through what that would take.</div>}
                <div className="bkd-trust" data-testid="booked-trust">
                  <div><Building2 size={20} aria-hidden="true" /><span>Compare 50+ lenders</span></div>
                  <div><MessageSquareText size={20} aria-hidden="true" /><span>Clear advice. Zero mortgage-speak.</span></div>
                  <div><Star size={20} aria-hidden="true" /><span>{reviews} five-star reviews</span></div>
                </div>
              </div>
              <AppointmentCard booking={booking} copy={copy} onUpdated={onUpdated} />
            </div>
          </section>

          {!cancelled && <section className="bkd-steps-sec">
            <div className="wrap">
              <div className="bkd-sec-head"><div className="eyebrow">What happens next</div><h2 className="display">Three things. None of them homework.</h2></div>
              <div className="booked-steps" data-testid="booked-steps">
                {copy.steps.map(([h, p], i) => <div className="booked-step" key={h}><span className="booked-num" aria-hidden="true">0{i + 1}</span><h3>{h}</h3><p>{p}</p></div>)}
              </div>
            </div>
          </section>}

          <section className="bkd-who" data-testid="booked-who">
            <div className="wrap bkd-who-grid">
              <div className="bkd-who-photo"><img src={MATT.photo} alt={MATT.name} width="280" height="280" /></div>
              <div>
                <div className="eyebrow">Who you’re booked with</div>
                <h2 className="display">Hi, I’m Matt.</h2>
                <p className="bkd-who-role">{MATT.name} · {MATT.role}</p>
                <p className="bkd-who-bio">I’m the mortgage broker you’ll be speaking with. My job is to make your options easier to understand and help you figure out your next move—without pressure or mortgage jargon.</p>
                <p className="bkd-who-bio">You don’t need all the answers before we talk. That’s what the conversation is for.</p>
                <p className="bkd-who-bio bkd-who-give">With every mortgage we fund, part of what we earn goes to Project CHEW, Camp fYrefly, the Stollery and Brokers Who Care. <SmartLink href="/why-hello/" data-testid="booked-about-link">More about the team →</SmartLink></p>
                <ul className="bkd-creds">{CREDS.map((c) => <li key={c}>{c}</li>)}</ul>
                <p className="bkd-who-call" data-testid="booked-who-call"><Phone size={18} aria-hidden="true" /><span>Your call will come from <a href={PHONE_TEL}>{PHONE}</a>. Save the number so you recognize it.</span></p>
              </div>
            </div>
          </section>

          <section className="bkd-prep">
            <div className="wrap bkd-prep-grid">
              {!cancelled && <div className="bkd-handy" data-testid="booked-before">
                <div className="eyebrow">Before we chat</div>
                <h2 className="display">Just bring your questions.</h2>
                <p className="bkd-before-lead">No documents to gather. No forms to finish. No need to have everything figured out.</p>
                <p>{copy.chat}</p>
                <p>{copy.together}</p>
                {!copy.realtor && <p className="bkd-fine">When you’re ready to apply, we’ll guide you through the documents needed in your secure application portal.</p>}
              </div>}
              {ctx && (
                <div className="bkd-wait" data-testid="booked-explore">
                  <div className="eyebrow">Explore at your own pace</div>
                  <h2 className="display">Curious about what comes next?</h2>
                  <p className="bkd-explore-lead">These tools and guides can help you get familiar with the basics. They’re completely optional—there’s nothing you need to finish before our call.</p>
                  <Modules confirm={ctx.confirm} />
                </div>
              )}
            </div>
          </section>

          <Testimonials limit={6} eyebrow="Straight from Google" heading="Our clients say it better." />

          <section className="bkd-close">
            <div className="wrap">
              <p data-testid="booked-closing-line">Something come up before the call? Text us at <a href={SMS} data-testid="booked-text-link">{PHONE}</a> — a real human reads it.</p>
              {!cancelled && <p className="bkd-ps" data-testid="booked-ps">Need another time? You can reschedule or cancel right from the card at the top of this page.</p>}
            </div>
          </section>
        </div>
      )}
    </SiteLayout>
  );
}
